import { Link, type LinkProps } from 'react-router-dom';
import type { Flow } from '../types';
import '../styles/flow.css';

interface FlowCardProps {
  flow: Flow;
  linkState?: LinkProps['state'];
  onNavigate?: () => void;
}

export function FlowCard({ flow, linkState, onNavigate }: FlowCardProps) {
  const placeholder = (
    <div className="flow-card__placeholder" aria-hidden="true">
      {flow.title.replace(/\b\w/g, c => c.toUpperCase())}
    </div>
  );
  const upvoteCount =
    typeof flow.upvoteCount === 'number' && Number.isFinite(flow.upvoteCount)
      ? flow.upvoteCount
      : 0;

  return (
    <Link
      to={`/flows/${flow.id}`}
      className="flow-card"
      state={linkState}
      onClickCapture={onNavigate}
    >
      <div className="flow-card__media">
        {flow.image ? (
          <img src={flow.image} alt={`${flow.title} preview`} className="flow-card__image" />
        ) : (
          placeholder
        )}
      </div>
      <div className="flow-card__body">
        <div className="flow-card__header">
          <div className="flow-card__title-group">
            <span
              className={`flow-card__kind-badge flow-card__kind-badge--${flow.kind === 'node' ? 'node' : 'flow'}`}
            >
              {flow.kind === 'node' ? 'Node' : 'Flow'}
            </span>
            <span
              className={`flow-card__visibility flow-card__visibility--${
                flow.visibility === 'team_restricted' ? 'team' : 'public'
              }`}
              title={
                flow.visibility === 'team_restricted' && flow.visibleTeams?.length
                  ? `Visible to: ${flow.visibleTeams.map((team) => team.name).join(', ')}`
                  : 'Visible to everyone'
              }
            >
              {flow.visibility === 'team_restricted'
                ? flow.visibleTeams?.map((team) => team.name).join(', ') || 'Team restricted'
                : 'Public'}
            </span>
          </div>
          <span className={`status status-${flow.status}`}>{flow.status}</span>
        </div>
        {flow.isPublishedView ? (
          <span className="flow-card__badge">Last approved version</span>
        ) : null}
        {flow.isRecommended ? (
          <span className="flow-card__badge flow-card__badge--recommended">Recommended</span>
        ) : null}
        {flow.madeByThales ? (
          <span className="flow-card__badge flow-card__badge--thales">Made by Thales</span>
        ) : null}
        <p className="flow-card__description">{flow.description || 'No description provided yet.'}</p>
        <div className="flow-card__meta">
          <span>Kind: {flow.kind === 'node' ? 'Node' : 'Flow'}</span>
          {flow.flowType ? (
            <span>
              Type: {flow.flowType}
              {flow.flowTypeDetail ? ` — ${flow.flowTypeDetail}` : ''}
            </span>
          ) : null}
          <span>Owner: {flow.createdByName}</span>
          <span>Updated: {new Date(flow.updatedAt).toLocaleDateString()}</span>
          <span>
            Upvotes: {upvoteCount.toLocaleString()}
            {flow.viewerHasUpvoted ? ' (You)' : ''}
          </span>
        </div>
        <div className="flow-card__tags">
          {flow.tags?.map((tag) => (
            <span key={tag} className="tag">
              {tag}
            </span>
          ))}
        </div>
      </div>
    </Link>
  );
}
